import { CommandId, type EnvironmentApi, type EnvironmentId } from "@cmmd-forge/contracts";
import { useEffect, useState } from "react";

import { prepareThreadExecutionIntentAction } from "~/lib/runThreadExecutionIntentAction";
import { newCommandId } from "~/lib/utils";

export interface ThreadTerminalEnvironmentExecution {
  readonly api: EnvironmentApi;
  readonly dispatch: <T>(action: (api: EnvironmentApi) => Promise<T>) => Promise<T>;
}

interface ThreadTerminalEnvironmentSnapshot {
  readonly targetKey: string;
  readonly execution: ThreadTerminalEnvironmentExecution | null;
  readonly error: string | null;
}

const TERMINAL_DISPATCH_IDENTITY_PREFIX = "forge:terminal-execution-intent:v1:";
const terminalDispatchIdentityFallback = new Map<string, CommandId>();

function terminalDispatchIdentityKey(environmentId: EnvironmentId, targetId: string): string {
  return `${TERMINAL_DISPATCH_IDENTITY_PREFIX}${environmentId}:${targetId}`;
}

function readOrCreateTerminalDispatchIdentity(
  environmentId: EnvironmentId,
  targetId: string,
  storage?: Pick<Storage, "getItem" | "setItem">,
): CommandId {
  const key = terminalDispatchIdentityKey(environmentId, targetId);
  const createIdentity = () => {
    const created = newCommandId();
    terminalDispatchIdentityFallback.set(key, created);
    return created;
  };
  try {
    const availableStorage =
      storage ?? (typeof window === "undefined" ? undefined : window.sessionStorage);
    const existing = availableStorage?.getItem(key)?.trim();
    if (existing) return CommandId.make(existing);
    const created = terminalDispatchIdentityFallback.get(key) ?? createIdentity();
    availableStorage?.setItem(key, created);
    return created;
  } catch {
    return terminalDispatchIdentityFallback.get(key) ?? createIdentity();
  }
}

/** Prepares one durable Terminal intent before a terminal session can open. */
export function useThreadTerminalEnvironmentApi(
  environmentId: EnvironmentId,
  projectId: number | null,
  targetId: string,
): {
  readonly execution: ThreadTerminalEnvironmentExecution | null;
  readonly error: string | null;
} {
  const targetKey = `${environmentId}:${targetId}`;
  const [snapshot, setSnapshot] = useState<ThreadTerminalEnvironmentSnapshot | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    const dispatchIdentity = readOrCreateTerminalDispatchIdentity(environmentId, targetId);
    void prepareThreadExecutionIntentAction(environmentId, {
      action: "terminal",
      projectId,
      targetId,
      dispatchIdentity,
      signal: controller.signal,
    })
      .then((prepared) => {
        if (controller.signal.aborted) return;
        if (prepared.api === null) {
          throw new Error("Forge could not restore the accepted terminal connection.");
        }
        setSnapshot({
          targetKey,
          execution: {
            api: prepared.api,
            dispatch: async <T>(action: (api: EnvironmentApi) => Promise<T>) => {
              const result = await prepared.dispatch(action, { replayDispatched: true });
              if (result.kind === "already-dispatched")
                throw new Error("Terminal dispatch failed.");
              return result.value;
            },
          },
          error: null,
        });
      })
      .catch((error: unknown) => {
        if (controller.signal.aborted) return;
        setSnapshot({
          targetKey,
          execution: null,
          error:
            error instanceof Error && error.message.trim().length > 0
              ? error.message
              : "Forge could not open this terminal.",
        });
      });
    return () => controller.abort();
  }, [environmentId, projectId, targetId, targetKey]);

  return snapshot?.targetKey === targetKey
    ? { execution: snapshot.execution, error: snapshot.error }
    : { execution: null, error: null };
}
